import React, { useState, useRef, useCallback } from 'react';
import supabase from '../lib/supabase';
import { areaToDisplay, areaUnitLabel, areaFromDisplay } from '../utils/unitConversion';
import { Lock, Lightbulb, Camera, Folder, Ruler, RefreshCw, Check } from 'lucide-react';
import '../styles/CookieAIReview.css';

const SUPABASE_FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL ||
  (process.env.REACT_APP_SUPABASE_URL || '').replace('/rest/v1', '') + '/functions/v1';

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.stl';

export default function CookieAIReview({ rowId, isMetric, aiModel, onAcceptArea }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [longestSide, setLongestSide] = useState('');
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileData, setFileData] = useState(null);   // { base64, mediaType } | { stlBuffer }
  const [isStl, setIsStl] = useState(false);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [editedArea, setEditedArea] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);

  // Once a file's picked (or analysis has run), the upfront privacy/tip banners
  // just eat space — collapse them to reopenable pills instead of full text.
  const hasStartedUpload = !!fileData || loading || !!result;

  const handleFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setError(null);
    setEditedArea('');

    if (file.name.toLowerCase().endsWith('.stl')) {
      setIsStl(true);
      setPreview(null);
      const reader = new FileReader();
      reader.onload = (e) => setFileData({ stlBuffer: e.target.result });
      reader.readAsArrayBuffer(file);
    } else {
      setIsStl(false);
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setPreview(dataUrl);
        setFileData({ base64: dataUrl.split(',')[1], mediaType: file.type || 'image/jpeg' });
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleChangeFile = () => {
    setFileName('');
    setFileData(null);
    setPreview(null);
    setIsStl(false);
    setResult(null);
    setError(null);
    setEditedArea('');
  };

  const handleAnalyze = async () => {
    if (!fileData) { setError('Please select a photo or STL file first.'); return; }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const authHeader = session?.access_token ? `Bearer ${session.access_token}` : '';

      const longestSideIn = longestSide
        ? isMetric ? parseFloat(longestSide) / 25.4 : parseFloat(longestSide)
        : 0;

      let response;
      if (isStl && fileData.stlBuffer) {
        const form = new FormData();
        form.append('file', new Blob([fileData.stlBuffer]), fileName);
        form.append('longest_side_in', String(longestSideIn || 0));
        form.append('row_id', rowId || '');
        response = await fetch(`${SUPABASE_FUNCTIONS_URL}/cookie-ai-size`, {
          method: 'POST',
          headers: { Authorization: authHeader },
          body: form,
        });
      } else {
        response = await fetch(`${SUPABASE_FUNCTIONS_URL}/cookie-ai-size`, {
          method: 'POST',
          headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_base64: fileData.base64,
            image_media_type: fileData.mediaType,
            longest_side_in: longestSideIn || 0,
            row_id: rowId || '',
          }),
        });
      }

      if (response.status === 429) {
        const data = await response.json();
        setError(data.message || 'Daily limit reached. Please try again tomorrow.');
        return;
      }

      const data = await response.json();
      if (data.error && data.area_sq_in == null) {
        setError(data.ai_notes || data.message || 'Something went wrong. Please try again.');
        return;
      }

      setResult(data);
      if (data.area_sq_in != null) {
        setEditedArea(areaToDisplay(data.area_sq_in, isMetric).toFixed(3));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptArea = () => {
    const displayVal = parseFloat(editedArea);
    if (isNaN(displayVal) || displayVal <= 0) return;
    onAcceptArea(areaFromDisplay(displayVal, isMetric), result?.ai_notes || '', result?.used_ai || false);
    setAccepted(true);
  };

  if (accepted) {
    return (
      <div className="cair-container cair-container--collapsed">
        <button type="button" className="cair-reopen-link" onClick={() => setAccepted(false)}>
          <Ruler size={14} className="cair-inline-icon" />
          {result?.used_ai ? 'AI' : 'Geometry'} area applied: <span className="cair-reopen-action">re-analyze</span>
        </button>
      </div>
    );
  }

  return (
    <div className="cair-container">

      {!hasStartedUpload ? (
        <>
          {/* ── Privacy notice ── */}
          <div className="cair-privacy-notice">
            <span>
              <Lock size={15} className="cair-inline-icon" />
              Your image is sent directly to AI for analysis and is <strong>never stored</strong> by us.
            </span>
            {' '}
            <button type="button" className="cair-privacy-toggle" onClick={() => setShowPrivacy(p => !p)}>
              {showPrivacy ? 'Hide details ▲' : 'Find out more ▼'}
            </button>
          </div>

          {/* ── Tip ── */}
          <div className="cair-tip">
            <Lightbulb size={15} className="cair-inline-icon" />
            For best results, place the cookie on a <strong>solid, contrasting background</strong>. STL files are measured geometrically, no AI required.
          </div>
        </>
      ) : (
        /* ── Condensed info pills once the flow is underway ── */
        <div className="cair-info-condensed">
          <button type="button" className="cair-info-pill" onClick={() => setShowPrivacy(p => !p)}>
            <Lock size={14} />Privacy {showPrivacy ? '▲' : '▼'}
          </button>
          <button type="button" className="cair-info-pill" onClick={() => setShowTip(t => !t)}>
            <Lightbulb size={14} />Tips {showTip ? '▲' : '▼'}
          </button>
        </div>
      )}

      {showPrivacy && (
        <div className="cair-privacy-detail">
          <p>
            When you tap "Analyze Cookie", your image is transmitted securely to our server and forwarded to the AI model for processing. <strong>We do not save your image</strong>. It exists only in memory during the request and is discarded immediately after.
          </p>
          <p>
            We use <strong>{aiModel || 'Claude (Anthropic)'}</strong> for this analysis. Like all AI providers, Anthropic may use submitted data to improve their models in accordance with their{' '}
            <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
            We cannot control how the AI provider uses submitted data on their end.
          </p>
          <p>
            For best privacy, use a photo that shows only the cookie and avoids personally identifiable information.
          </p>
        </div>
      )}

      {hasStartedUpload && showTip && (
        <div className="cair-tip">
          <Lightbulb size={15} className="cair-inline-icon" />
          For best results, place the cookie on a <strong>solid, contrasting background</strong>. STL files are measured geometrically, no AI required.
        </div>
      )}

      {/* Hidden inputs — always present so "Change file" can reopen the picker */}
      <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} style={{ display: 'none' }}
        onChange={(e) => handleFile(e.target.files?.[0])} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
        onChange={(e) => handleFile(e.target.files?.[0])} />

      {!hasStartedUpload ? (
        <>
          {/* ── Drop zone ── */}
          <div
            className="cair-dropzone"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="cair-dropzone-prompt">
              <span className="cair-dropzone-icon"><Camera size={30} /></span>
              <span>Drop a photo or STL here, or click to browse</span>
            </div>
          </div>

          {/* ── Upload buttons ── */}
          <div className="cair-upload-row">
            <button type="button" className="cair-btn cair-btn--secondary" onClick={() => fileInputRef.current?.click()}>
              <Folder size={18} />Upload File
            </button>
            <button type="button" className="cair-btn cair-btn--secondary" onClick={() => cameraInputRef.current?.click()}>
              <Camera size={18} />Take Photo
            </button>
          </div>

          {/* ── Longest side — hidden for STL (not needed, geometry handles it) ── */}
          {!isStl && (
            <div className="cair-measurement-row">
              <label className="cair-label">
                Longest side <span className="unit-label">({isMetric ? 'mm' : 'in'})</span>
                <span className="cair-hint">, optional but improves accuracy</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={longestSide}
                onChange={(e) => setLongestSide(e.target.value)}
                className="cair-input"
                placeholder={isMetric ? 'e.g. 76.2' : 'e.g. 3.0'}
              />
            </div>
          )}
        </>
      ) : (
        /* ── Condensed file summary once uploading/analysing/reviewing ── */
        <div className="cair-file-summary">
          {preview && <img src={preview} alt="Cookie preview" className="cair-file-summary-thumb" />}
          <span className="cair-file-summary-text">
            {isStl
              ? <Ruler size={15} className="cair-inline-icon" />
              : <Camera size={15} className="cair-inline-icon" />}
            {fileName}{loading ? ', analysing…' : ''}
          </span>
          <button type="button" className="cair-change-file-link" onClick={handleChangeFile} disabled={loading}>
            Change file
          </button>
        </div>
      )}

      {/* ── Analyze button — shown until a result comes back, collapsed view included ── */}
      {!result && (
        <button
          type="button"
          className="cair-btn cair-btn--primary"
          onClick={handleAnalyze}
          disabled={loading || !fileData}
        >
          {loading
            ? <><RefreshCw size={18} className="cair-icon-spin" />Analysing…</>
            : <><Ruler size={18} />{error ? 'Retry' : 'Estimate Size'}</>}
        </button>
      )}

      {/* ── Error ── */}
      {error && <div className="cair-error">{error}</div>}

      {/* ── Results ── */}
      {result && (
        <div className="cair-results">
          {result.used_ai && <div className="cair-ai-badge">AI Estimated</div>}
          {!result.used_ai && (
            <div className="cair-ai-badge cair-ai-badge--geo">
              <Ruler size={14} />Geometry Computed
            </div>
          )}

          {result.area_sq_in != null && (
            <div className="cair-result-block">
              <label className="cair-label">
                Estimated Cookie Area <span className="unit-label">({areaUnitLabel(isMetric)})</span>
              </label>
              <div className="cair-area-row">
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={editedArea}
                  onChange={(e) => setEditedArea(e.target.value)}
                  className="cair-input cair-area-input"
                />
                <button type="button" className="cair-btn cair-btn--accept" onClick={handleAcceptArea}>
                  <Check size={18} />Use This Area
                </button>
              </div>
              <span className="cair-hint">You can edit this value before accepting.</span>
            </div>
          )}

          <button type="button" className="cair-notes-toggle" onClick={() => setShowNotes(n => !n)}>
            {showNotes ? '▲ Hide Notes' : '▼ Show Notes'}
          </button>
          {showNotes && (
            <div className="cair-notes">{result.ai_notes || 'No additional notes.'}</div>
          )}
        </div>
      )}
    </div>
  );
}
